import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { loadAirlines, logoUrl } from "@/lib/flights";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const Row = ({ a, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 300 * 1024) { toast.error("El logo debe pesar máximo 300 KB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try { await api.put(`/airlines/${a.code}/logo`, { logo_data: reader.result }); setV((x) => x + 1); onChanged(a.code, true); toast.success(`Logo de ${a.name} guardado`); }
      catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar el logo"); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/airlines/${a.code}/logo`); onChanged(a.code, false); toast.success("Logo quitado"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-4 border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`airline-row-${a.code}`}>
      <span className="grid h-10 w-16 shrink-0 place-items-center rounded-md bg-slate-50">
        {a.has_logo ? <img src={`${logoUrl(a.code)}?v=${v}`} alt="" className="h-9 w-14 object-contain" data-testid={`airline-logo-${a.code}`} /> : <span className="font-mono text-xs text-slate-400">{a.code}</span>}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">{a.name}</p>
        <p className="text-xs text-slate-500">{a.code}{a.country ? ` · ${a.country}` : ""}</p>
      </div>
      <label className={`inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${busy ? "pointer-events-none opacity-50" : ""}`} data-testid={`airline-upload-${a.code}`}>
        <Upload size={14} className="mr-1.5" />{a.has_logo ? "Cambiar" : "Subir logo"}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} />
      </label>
      {a.has_logo && <Button variant="ghost" size="sm" disabled={busy} onClick={remove} data-testid={`airline-remove-${a.code}`}><X size={14} /></Button>}
    </div>
  );
};

export default function Airlines() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [onlyLogo, setOnlyLogo] = useState(false);
  useEffect(() => { loadAirlines(true).then(setList).catch(() => toast.error("No se pudieron cargar las aerolíneas")); }, []);
  const shown = useMemo(() => {
    const n = norm(q).trim();
    return list.filter((a) => (!onlyLogo || a.has_logo) && (!n || norm(`${a.code} ${a.name} ${a.country}`).includes(n))).slice(0, 200);
  }, [list, q, onlyLogo]);
  const onChanged = (code, has) => { setList((l) => l.map((a) => (a.code === code ? { ...a, has_logo: has } : a))); loadAirlines(true); };
  return (
    <>
      <PageHeader title="Aerolíneas" subtitle="Sube el logo de cada aerolínea para que aparezca en la página de pago, los correos, el recibo y los documentos de confirmación." />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca por nombre, código o país" className="pl-9" data-testid="airlines-search" />
        </div>
        <Button variant={onlyLogo ? "default" : "outline"} onClick={() => setOnlyLogo((x) => !x)} data-testid="airlines-only-logo">Solo con logo</Button>
      </div>
      <p className="mb-2 text-xs text-slate-500" data-testid="airlines-count">{list.length} aerolíneas · {list.filter((a) => a.has_logo).length} con logo{shown.length === 200 ? " · mostrando las primeras 200, usa el buscador" : ""}</p>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="airlines-list">
        {shown.map((a) => <Row key={a.code} a={a} onChanged={onChanged} />)}
        {shown.length === 0 && <p className="p-8 text-center text-sm text-slate-500">Sin resultados</p>}
      </div>
    </>
  );
}
